import { useState, useEffect, useRef } from 'react';
import type { GrandSnatchState } from '../game/types';

interface TutorialStep {
  id: string;
  phase: 'draft' | 'play' | 'stage' | 'gameover' | 'any';
  text: string;
  subtext?: string;
  // When true the step auto-advances when the game state changes to the
  // specified phase (checked after mount).
  autoOnPhase?: 'play' | 'stage' | 'gameover';
  position: 'center' | 'top-center' | 'bottom-center' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  targetSelector?: string; // CSS selector of the DOM element to spotlight
}

const STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    phase: 'draft',
    text: 'Welcome to GrandSnatch!',
    subtext:
      'A strategic card extraction game of heists and turf wars. ' +
      'You are a gang leader competing to capture resources from the streets and complete heist missions.',
    position: 'center',
  },
  {
    id: 'suits',
    phase: 'draft',
    text: 'Form Your Crew (Deck)',
    subtext:
      'Select 2 suits below to assemble your deck. ' +
      'Each suit gives you 6 Action cards (values 1–6) and 1 Mission card (your end-game heist objective). ' +
      'Select your suits, click "Confirm Deck" and wait for your opponent.',
    position: 'center',
    targetSelector: '#tutorial-suit-picker',
  },
  {
    id: 'streets_layout',
    phase: 'play',
    text: 'Welcome to the Streets',
    subtext:
      'The match is on! Both players will play cards facedown simultaneously, targeting one of the two Dropzones in the center. ' +
      'Let\'s review the layout of the board.',
    autoOnPhase: 'play',
    position: 'center',
  },
  {
    id: 'dropzones',
    phase: 'play',
    text: 'The Dropzones',
    subtext:
      'These two central areas represent the active streets. The cards currently lying here are up for grabs. ' +
      'You can target either Dropzone for your plays.',
    position: 'top-center',
    targetSelector: '.dropzones',
  },
  {
    id: 'hand_and_play',
    phase: 'play',
    text: 'Making a Play',
    subtext:
      'Drag an Action card from your hand (at the bottom) to a Dropzone. ' +
      'Your card captures table cards whose values sum up exactly to yours. ' +
      'If a card with the exact same value is on the table, you must take that single card.',
    position: 'bottom-center',
    targetSelector: '.my-hand',
  },
  {
    id: 'staging',
    phase: 'play',
    text: 'Staging Area & Stash',
    subtext:
      'Captured cards are placed in your Staging Area (on the right) temporarily. ' +
      'Once the round resolves, they are moved to your Stash as resources (Drugs, Cars, Money, etc.) ' +
      'which you can see under your profile avatar.',
    position: 'bottom-right',
    targetSelector: '.my-staging',
  },
  {
    id: 'snatch',
    phase: 'play',
    text: '⚡ The Grand Snatch!',
    subtext:
      'If your play captures ALL cards on a Dropzone, leaving it empty, you execute a Snatch! ' +
      'This awards you +1 Snatch Point immediately. Snatches are tracked on your profile (bottom-left).',
    position: 'bottom-left',
    targetSelector: '.player-panel-me',
  },
  {
    id: 'missions_location',
    phase: 'play',
    text: 'Missions & Location Goals',
    subtext:
      'Each Dropzone holds a Mission card. There is also a global Location goal at the top. ' +
      'Win these objectives at the end of the match for massive bonus points. ' +
      'Go ahead, drag a card and confirm your play!',
    position: 'top-center',
    targetSelector: '.location-banner',
  },
];

const getInitialStepIndex = (phase: string): number => {
  if (phase === 'play') {
    const idx = STEPS.findIndex(s => s.autoOnPhase === 'play');
    return idx !== -1 ? idx : 0;
  }
  return 0;
};

interface TutorialOverlayProps {
  G: GrandSnatchState;
  phase: string;
  gameover: unknown;
  onDismiss: () => void;
}

export function TutorialOverlay({ G, phase, gameover, onDismiss }: TutorialOverlayProps) {
  const [stepIndex, setStepIndex] = useState(() => getInitialStepIndex(phase));
  const prevPhaseRef = useRef(phase);
  const [spotlightBounds, setSpotlightBounds] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const currentStep = STEPS[stepIndex];

  // Auto-advance when the game phase changes to the expected phase.
  useEffect(() => {
    if (prevPhaseRef.current === phase) return;
    prevPhaseRef.current = phase;
    const targetIdx = STEPS.findIndex(s => s.autoOnPhase === phase);
    if (targetIdx !== -1 && stepIndex < targetIdx) {
      setStepIndex(targetIdx);
    }
  }, [phase, stepIndex]);

  // Auto-advance on gameover — jump past all remaining steps.
  useEffect(() => {
    if (gameover) setStepIndex(STEPS.length); // exhausted → overlay hides itself
  }, [gameover]);

  // Calculate and update bounds of target element to highlight
  useEffect(() => {
    const updateBounds = () => {
      if (!currentStep?.targetSelector) {
        setSpotlightBounds(null);
        return;
      }
      const el = document.querySelector(currentStep.targetSelector);
      if (el) {
        const rect = el.getBoundingClientRect();
        const padding = 6;
        setSpotlightBounds({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        });
      } else {
        setSpotlightBounds(null);
      }
    };

    updateBounds();
    const timer = setTimeout(updateBounds, 120); // slight delay to allow layout animations to settle

    window.addEventListener('resize', updateBounds);
    window.addEventListener('scroll', updateBounds, { capture: true, passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateBounds);
      window.removeEventListener('scroll', updateBounds, { capture: true });
    };
  }, [stepIndex, currentStep]);

  // Hide once all steps are exhausted or gameover.
  if (!currentStep || stepIndex >= STEPS.length) return null;

  // Don't show a step that belongs to a phase we haven't reached yet.
  if (currentStep.phase !== 'any' && currentStep.phase !== phase && currentStep.phase !== 'gameover') return null;

  // Unused in render but prevents the ESLint exhaustive-deps warning.
  void G;

  const isLast = stepIndex >= STEPS.length - 1;

  // Compute container positioning style based on step configuration
  let containerStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 8000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none',
    width: '100vw',
    transition: 'all 0.3s ease-in-out',
  };

  switch (currentStep.position) {
    case 'center':
      containerStyle = {
        ...containerStyle,
        inset: 0,
        justifyContent: 'center',
      };
      break;
    case 'top-center':
      containerStyle = {
        ...containerStyle,
        top: '120px',
        left: '50%',
        transform: 'translateX(-50%)',
      };
      break;
    case 'bottom-center':
      containerStyle = {
        ...containerStyle,
        bottom: '220px',
        left: '50%',
        transform: 'translateX(-50%)',
      };
      break;
    case 'bottom-left':
      containerStyle = {
        ...containerStyle,
        bottom: '160px',
        left: '20px',
        alignItems: 'flex-start',
        width: 'auto',
      };
      break;
    case 'bottom-right':
      containerStyle = {
        ...containerStyle,
        bottom: '160px',
        right: '20px',
        alignItems: 'flex-end',
        width: 'auto',
      };
      break;
    case 'top-left':
      containerStyle = {
        ...containerStyle,
        top: '70px',
        left: '20px',
        alignItems: 'flex-start',
        width: 'auto',
      };
      break;
    case 'top-right':
      containerStyle = {
        ...containerStyle,
        top: '150px',
        right: '20px',
        alignItems: 'flex-end',
        width: 'auto',
      };
      break;
  }

  return (
    <>
      {/* Background Dim / Spotlight Overlay */}
      {spotlightBounds ? (
        <>
          {/* Top overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: Math.max(0, spotlightBounds.top),
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              zIndex: 7500,
              pointerEvents: 'auto',
              transition: 'all 0.25s ease-in-out',
            }}
          />
          {/* Bottom overlay */}
          <div
            style={{
              position: 'fixed',
              top: spotlightBounds.top + spotlightBounds.height,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              zIndex: 7500,
              pointerEvents: 'auto',
              transition: 'all 0.25s ease-in-out',
            }}
          />
          {/* Left overlay */}
          <div
            style={{
              position: 'fixed',
              top: spotlightBounds.top,
              left: 0,
              width: Math.max(0, spotlightBounds.left),
              height: spotlightBounds.height,
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              zIndex: 7500,
              pointerEvents: 'auto',
              transition: 'all 0.25s ease-in-out',
            }}
          />
          {/* Right overlay */}
          <div
            style={{
              position: 'fixed',
              top: spotlightBounds.top,
              left: spotlightBounds.left + spotlightBounds.width,
              right: 0,
              height: spotlightBounds.height,
              backgroundColor: 'rgba(0, 0, 0, 0.65)',
              zIndex: 7500,
              pointerEvents: 'auto',
              transition: 'all 0.25s ease-in-out',
            }}
          />
          {/* Highlight border and glow around target */}
          <div
            style={{
              position: 'fixed',
              top: spotlightBounds.top,
              left: spotlightBounds.left,
              width: spotlightBounds.width,
              height: spotlightBounds.height,
              borderRadius: 8,
              border: '2px solid var(--accent-color)',
              boxShadow: '0 0 20px var(--accent-color), inset 0 0 10px rgba(255, 255, 255, 0.1)',
              pointerEvents: 'none',
              zIndex: 7501,
              transition: 'all 0.25s ease-in-out',
            }}
          />
        </>
      ) : (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 7500,
            background: 'rgba(0, 0, 0, 0.65)',
            pointerEvents: 'auto',
          }}
        />
      )}

      {/* Tutorial Popup Box */}
      <div style={containerStyle}>
        <div
          style={{
            pointerEvents: 'all',
            background: '#0c0c18',
            border: '2px solid var(--accent-color)',
            borderRadius: 10,
            padding: '18px 24px',
            maxWidth: 480,
            width: '92vw',
            boxShadow: '0 4px 32px rgba(0,0,0,0.7)',
            position: 'relative',
          }}
        >
          {/* Step counter */}
          <div style={{ position: 'absolute', top: 10, right: 14, fontSize: 10, color: '#666' }}>
            {stepIndex + 1} / {STEPS.length}
          </div>

          {/* Content */}
          <div style={{ fontWeight: 'bold', color: 'var(--accent-color)', fontSize: 15, marginBottom: 6 }}>
            {currentStep.text}
          </div>
          {currentStep.subtext && (
            <div style={{ color: '#ccc', fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
              {currentStep.subtext}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
            <button
              onClick={onDismiss}
              style={{
                background: 'none', border: '1px solid #444', color: '#777',
                padding: '5px 12px', borderRadius: 4, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 11,
              }}
            >
              Skip tutorial
            </button>
            <button
              onClick={() => {
                if (isLast) { onDismiss(); } else { setStepIndex(i => i + 1); }
              }}
              className="multi-btn"
              style={{ fontSize: 12, padding: '6px 16px' }}
            >
              {isLast ? 'Got it!' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

